import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Divider,
  Box,
  Typography,
  Avatar,
  CircularProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const AddModal = ({ open, onClose, title, fields, values, onChange, onSubmit, docUploading }) => {
  const [entries, setEntries] = useState([{}]);

  const handleAddEntry = () => {
    setEntries([...entries, {}]);
  };

  const handleRemoveEntry = (index) => {
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
    onChange('entries', newEntries);
  };

  const handleEntryChange = (index, fieldName, value) => {
    const newEntries = [...entries];
    newEntries[index] = {
      ...newEntries[index],
      [fieldName]: value
    };
    setEntries(newEntries);
    onChange('entries', newEntries);
  };

  const renderField = (field, value, entryIndex) => {
    switch (field.type) {
      case 'date':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={field.label}
              value={value || null}
              onChange={(date) => handleEntryChange(entryIndex, field.name, date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </LocalizationProvider>
        );
      
      case 'select':
        return (
          <FormControl fullWidth>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value || ''}
              onChange={(e) => handleEntryChange(entryIndex, field.name, e.target.value)}
              label={field.label}
            >
              {field.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'file':
        return (
          <Box>
            {entries[entryIndex][field.name]?.docPath && (
              <Box 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  mb: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1
                }}
              >
                <Avatar 
                  src={entries[entryIndex][field.name].docPath} 
                  variant="rounded"
                  sx={{ width: 40, height: 40 }}
                />
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  {entries[entryIndex][field.name].docName || 'Uploaded Document'}
                </Box>
              </Box>
            )}
            <Button
              variant="outlined"
              component="label"
              fullWidth
              disabled={docUploading}
              startIcon={docUploading && <CircularProgress size={20} />}
              sx={{ 
                height: '56px',
                justifyContent: 'flex-start',
                textAlign: 'left'
              }}
            >
              {docUploading ? "Uploading..." : field.label}
              <input
                type="file"
                hidden
                accept={field.accept}
                onChange={(e) => field.onChange(e, entryIndex, handleEntryChange)}
              />
            </Button>
          </Box>
        );

      case 'uploadButton':
        const Component = field.component;
        return (
          <Box sx={{ mt: 1 }}>
            {entries[entryIndex][field.name]?.docPath && (
              <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2">
                  Current file: {entries[entryIndex][field.name].docName}
                </Typography>
              </Box>
            )}
            <Component {...field.props} />
          </Box>
        );

      default:
        return (
          <TextField
            fullWidth
            label={field.label}
            value={value || ''}
            onChange={(e) => handleEntryChange(entryIndex, field.name, e.target.value)}
            multiline={field.multiline}
            rows={field.multiline ? 4 : 1}
          />
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {entries.map((entry, entryIndex) => (
          <Box key={entryIndex} sx={{ mb: 3 }}>
            {entryIndex > 0 && <Divider sx={{ my: 3 }} />}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">Entry {entryIndex + 1}</Typography>
              {entries.length > 1 && (
                <IconButton 
                  onClick={() => handleRemoveEntry(entryIndex)}
                  color="error"
                  size="small"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              )}
            </Box>
            <Stack spacing={3}>
              {fields.map((field) => (
                <div key={field.name}>
                  {renderField(field, entry[field.name], entryIndex)}
                </div>
              ))}
            </Stack>
          </Box>
        ))}
        <Button
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddEntry}
          sx={{ mt: 2 }}
        >
          Add Another Entry
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={docUploading}>Cancel</Button>
        <Button 
          onClick={() => onSubmit(entries)} 
          variant="contained" 
          color="primary"
          disabled={docUploading}
        >
          Add All
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddModal;